/**
 * Visual Service - Unified service for Wireframes, Designs & Design System
 * =========================================================================
 *
 * This unified service handles all visual page types through a single API pattern.
 *
 * Supported task types:
 * - wireframes: Grayscale wireframe pages
 * - designs: Full UI/UX design pages with colors
 * - design-system: Design system documentation pages
 */

import api from './api';

const BACKEND_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8003/api').replace(/\/api\/?$/, '');

// Task type configuration for frontend
const TASK_CONFIG = {
  'wireframes': {
    title: 'Generated Wireframes',
    icon: 'Web',
    downloadLabel: 'Download All Wireframes',
    emptyMessage: 'No wireframes available yet.',
    emptySubMessage: 'Wireframes will appear here after generation.',
    tipMessage: 'Click "View All Wireframes" to see the complete navigation index.',
    categories: ['landing', 'authentication', 'dashboard', 'profile', 'admin', 'error', 'other']
  },
  'designs': {
    title: 'UI/UX Designs',
    icon: 'Brush',
    downloadLabel: 'Download All Designs',
    emptyMessage: 'No design pages available yet.',
    emptySubMessage: 'Design pages will appear here after generation.',
    tipMessage: 'Click "View All Designs" to see the complete design navigation.',
    categories: ['landing', 'authentication', 'dashboard', 'profile', 'admin', 'error', 'other']
  },
  'design-system': {
    title: 'Design System',
    icon: 'Palette',
    downloadLabel: 'Download Design System',
    emptyMessage: 'No design system pages available yet.',
    emptySubMessage: 'Design system pages will appear here after generation.',
    tipMessage: 'Browse foundation, components, patterns, and guidelines.',
    categories: ['foundation', 'component', 'pattern', 'guideline', 'index', 'other']
  }
};

const visualService = {
  /**
   * Get task configuration for a specific task type
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @returns {Object} - Task configuration
   */
  getTaskConfig: (taskType) => {
    return TASK_CONFIG[taskType] || TASK_CONFIG['wireframes'];
  },

  /**
   * Get all supported task types
   * @returns {string[]} - Array of supported task types
   */
  getSupportedTaskTypes: () => {
    return Object.keys(TASK_CONFIG);
  },

  /**
   * List all pages for a project and task type
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @returns {Promise<Object>} - List of pages
   */
  listPages: async (projectId, taskType) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/pages`);
      return response.data;
    } catch (error) {
      console.error(`Error listing ${taskType} pages:`, error);
      throw error.response?.data?.message || `Failed to list ${taskType} pages`;
    }
  },

  /**
   * Get pages with URLs for a project and task type
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @returns {Promise<Object>} - Pages with URLs
   */
  getPageUrls: async (projectId, taskType) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/page-urls`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${taskType} page URLs:`, error);
      throw error.response?.data?.message || `Failed to get ${taskType} page URLs`;
    }
  },

  /**
   * Get HTML content for a specific page
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @param {string} pageName - Page name (without .html extension)
   * @returns {Promise<Object>} - Page content and metadata
   */
  getPageContent: async (projectId, taskType, pageName) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/pages/${pageName}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${taskType} page content:`, error);
      throw error.response?.data?.message || `Failed to get ${taskType} page content`;
    }
  },

  /**
   * Edit a page using Claude Code SDK surgical edit (Legacy - Direct Save)
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @param {string} pageName - Page name to edit
   * @param {string} editRequest - Natural language edit request
   * @returns {Promise<Object>} - Edit result
   */
  editPage: async (projectId, taskType, pageName, editRequest) => {
    try {
      const response = await api.put(
        `/api/visual-pages/projects/${projectId}/${taskType}/pages/${pageName}`,
        { edit_request: editRequest }
      );
      return response.data;
    } catch (error) {
      console.error(`Error editing ${taskType} page:`, error);
      throw error.response?.data?.message || `Failed to edit ${taskType} page`;
    }
  },

  /**
   * Preview a page edit WITHOUT saving to disk
   * Returns the modified HTML content for preview purposes
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @param {string} pageName - Page name to preview edit
   * @param {string} editRequest - Natural language edit request
   * @returns {Promise<Object>} - Preview result with original_content and preview_content
   */
  previewPageEdit: async (projectId, taskType, pageName, editRequest) => {
    try {
      const response = await api.post(
        `/api/visual-pages/projects/${projectId}/${taskType}/pages/${pageName}/preview`,
        { edit_request: editRequest }
      );
      return response.data;
    } catch (error) {
      console.error(`Error previewing ${taskType} page edit:`, error);
      throw error.response?.data?.message || `Failed to preview ${taskType} page edit`;
    }
  },

  /**
   * Confirm and save the previewed edit to disk
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @param {string} pageName - Page name to save
   * @param {string} previewContent - The previewed HTML content to save
   * @returns {Promise<Object>} - Confirm result
   */
  confirmPageEdit: async (projectId, taskType, pageName, previewContent) => {
    try {
      const response = await api.post(
        `/api/visual-pages/projects/${projectId}/${taskType}/pages/${pageName}/confirm`,
        { preview_content: previewContent }
      );
      return response.data;
    } catch (error) {
      console.error(`Error confirming ${taskType} page edit:`, error);
      throw error.response?.data?.message || `Failed to confirm ${taskType} page edit`;
    }
  },

  /**
   * Get the URL to display a page in iframe
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @param {string} pageName - Page name (default: 'index')
   * @returns {string} - Full URL to page
   */
  getPageUrl: (projectId, taskType, pageName = 'index') => {
    const subfolderMap = {
      'wireframes': 'wireframes',
      'designs': 'designs',
      'design-system': 'design-system'
    };
    const subfolder = subfolderMap[taskType] || taskType;
    return `${BACKEND_BASE_URL}/${subfolder}/${projectId}/${subfolder}/${pageName}.html`;
  },

  /**
   * Get shell.html content for a project
   * @param {string} projectId
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @returns {Promise<Object>} - { success, data: { html_content, has_shell } }
   */
  getShellContent: async (projectId, taskType) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/shell`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${taskType} shell:`, error);
      return { success: false, data: { has_shell: false } };
    }
  },

  /**
   * Get wireframe mapping for page confirmation
   * @param {string} projectId
   * @param {string} taskType
   * @returns {Promise<Object>} - { success, data: { mapping, pages, suggested, coverage } }
   */
  getWireframeMapping: async (projectId, taskType) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/mapping`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${taskType} mapping:`, error);
      throw error.response?.data?.message || `Failed to get ${taskType} mapping`;
    }
  },

  /**
   * Get supported task types from backend
   * @returns {Promise<Object>} - Task types info
   */
  getTaskTypesInfo: async () => {
    try {
      const response = await api.get('/api/visual-pages/task-types');
      return response.data;
    } catch (error) {
      console.error('Error getting task types info:', error);
      throw error.response?.data?.message || 'Failed to get task types info';
    }
  },

  /**
   * Debug endpoint to check project structure
   * @param {string} projectId - Project ID
   * @param {string} taskType - 'wireframes' | 'designs' | 'design-system'
   * @returns {Promise<Object>} - Debug info
   */
  debugProject: async (projectId, taskType) => {
    try {
      const response = await api.get(`/api/visual-pages/projects/${projectId}/${taskType}/debug`);
      return response.data;
    } catch (error) {
      console.error(`Error debugging ${taskType} project:`, error);
      throw error.response?.data?.message || `Failed to debug ${taskType} project`;
    }
  }
};

export default visualService;
